import { useState, useCallback, useEffect } from 'react';
import projectService from '../services/projectService';
import logger from '../utils/logger';

/**
 * Custom hook for managing project data and operations
 * Handles fetching, sorting, deleting, and viewing projects
 */
export const useProjectManagement = (user, isAuthenticated) => {
  const [myProjects, setMyProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [showProjectDetailPage, setShowProjectDetailPage] = useState(false);
  const [synopsisProjectDetails, setSynopsisProjectDetails] = useState(null);

  // Stable user id so we don't refetch when auth context re-renders with a new user object reference
  const userId = user?.id ?? user?.user_id ?? user?.sub ?? null;

  // Fetch projects from Supabase with intelligent caching (use userId so deps are stable and we avoid refetch on every auth re-render)
  const fetchMyProjects = useCallback(async (forceRefresh = false) => {
    if (userId && isAuthenticated()) {
      setProjectsLoading(true);
      try {
        const projects = await projectService.getUserProjects(forceRefresh);
        const formattedProjects = projectService.formatProjectData(projects);
        setMyProjects(formattedProjects);
      } catch (error) {
        logger.error('❌ fetchMyProjects: Error loading projects:', error);
        setMyProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    }
  }, [userId, isAuthenticated]);

  // Load projects on mount and when user identity changes (not on every auth re-render)
  useEffect(() => {
    fetchMyProjects();
  }, [fetchMyProjects]);

  // Sort projects
  const sortProjects = useCallback((projects, field, direction) => {
    return [...projects].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      // Handle date fields
      if (field === 'createdAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle string fields (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, []);

  // Handle sort field change
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Delete project
  const handleDeleteProject = useCallback(async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await projectService.deleteProject(projectId);
      logger.log(`✅ Project ${projectId} deleted successfully`);
      
      // Remove from local state immediately for better UX
      setMyProjects(prev => prev.filter(p => p.id !== projectId));
      
      // Also close detail view if this project was open
      if (selectedProject?.id === projectId) {
        setShowProjectDetail(false);
        setSelectedProject(null);
      }
      if (synopsisProjectDetails?.id === projectId) {
        setShowProjectDetailPage(false);
        setSynopsisProjectDetails(null);
      }
      
      // Refresh projects list to ensure sync
      setTimeout(() => fetchMyProjects(true), 500);
    } catch (error) {
      logger.error('❌ Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  }, [selectedProject, synopsisProjectDetails, fetchMyProjects]);

  // View project details
  const handleViewProject = useCallback(async (project) => {
    logger.log('👁️ Viewing project:', project);
    logger.log('🔍 Project type:', project.type);
    logger.log('🔍 Project projectType:', project.projectType);
    
    if (project.type === 'synopsis' || project.projectType === 'synopsis' || project.projectType === 'synopsis_generation' || project.projectType === 'synopsis_comparison') {
      // For synopsis projects, fetch full details and show detail page
      logger.log('📝 Detected synopsis project, fetching details...');
      setProjectsLoading(true);
      try {
        const details = await projectService.getSynopsisProjectDetails(project.id);
        logger.log('📄 Synopsis project details:', details);
        setSelectedProject(project);
        logger.log('✅ Set selectedProject:', project);
        setSynopsisProjectDetails(details);
        logger.log('✅ Set synopsisProjectDetails');
        setShowProjectDetailPage(true);
        logger.log('✅ Set showProjectDetailPage to true');
      } catch (error) {
        logger.error('❌ Error fetching synopsis project details:', error);
        alert('Failed to load project details. Please try again.');
      } finally {
        setProjectsLoading(false);
      }
    } else {
      // For transcription projects, show modal
      logger.log('🎤 Detected transcription project, showing modal...');
      setSelectedProject(project);
      setShowProjectDetail(true);
    }
  }, []);

  // Check if project name exists
  const checkProjectNameExists = useCallback((name) => {
    return myProjects.some(project => 
      project.title && project.title.toLowerCase() === name.toLowerCase()
    );
  }, [myProjects]);

  return {
    // State
    myProjects,
    projectsLoading,
    sortField,
    sortDirection,
    selectedProject,
    showProjectDetail,
    showProjectDetailPage,
    synopsisProjectDetails,
    
    // Setters
    setMyProjects,
    setProjectsLoading,
    setSelectedProject,
    setShowProjectDetail,
    setShowProjectDetailPage,
    setSynopsisProjectDetails,
    
    // Functions
    fetchMyProjects,
    sortProjects,
    handleSort,
    handleDeleteProject,
    handleViewProject,
    checkProjectNameExists,
  };
};

